import {
  ClickCollection,
  Collections,
  ContentCollection,
  DataLayerServiceEvent,
  EventInfoCollection,
  ListProduct,
  PageCollection,
  ScenarioName,
} from '@mindshift/datalayer';
import { TENANT } from 'helper/js/constants';
import { getVisiblePagesAmount, isAnimatedLinkType } from 'helper/js/helper';
import { Component, ComponentType, createContext, PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { Metric } from 'web-vitals/src/types';

import Product from '../../service/Product';
import ProductVariant from '../../service/ProductVariant';

export type ITRACKING = {
  eventCategory: string;
  eventAction: string;
  eventLabel?: string;
};

type ATrackingProduct = AProductData & { isAnimated?: ALinkDisplayTypes };

type TrackingProviderProps = {
  gtmId: string;
  debugTracking: boolean;
  cookieConsentGtmAttributes?: string;
  uuid?: string;
  id: string;
  pages: APagesItem[];
  isWideScreen: boolean;
  flippyPageNumber: number;
};

export type TrackingContextProps = {
  trackEventUniversalAnalytics: (data: ITRACKING) => void;
  trackEventDataLayerService: (
    eventCollections: Collections,
    content?: ContentCollection,
    eventInfo?: EventInfoCollection,
    products?: AProductData[],
    click?: ClickCollection,
    page?: PageCollection,
  ) => void;
  trackAddToCartEvent: (productVariant: Product | ProductVariant) => void;
};

export const TrackingContext = createContext<TrackingContextProps>({
  trackEventUniversalAnalytics: (data: ITRACKING) => {},
  trackEventDataLayerService: (
    eventCollections: Collections,
    content?: ContentCollection,
    eventInfo?: EventInfoCollection,
    products?: AProductData[],
    click?: ClickCollection,
    page?: PageCollection,
  ) => {},
  trackAddToCartEvent: (productVariant: Product | ProductVariant) => {},
});

const DATA_LAYER_SERVICE_MANIFEST = 'manifest.json';
class TrackingProvider extends Component<PropsWithChildren<TrackingProviderProps>, {}> {
  contextData: TrackingContextProps;

  initialized = false;
  isDataLayerServiceInitialized = false;
  isDataLayerServiceEnabled = true;

  eventQueueUniversalAnalytics: ITRACKING[] = [];
  eventQueueDataLayerService: {
    eventCollections: Collections;
    content?: ContentCollection;
    eventInfo?: EventInfoCollection;
    products?: AProductData[];
    click?: ClickCollection;
    page?: PageCollection;
  }[] = [];

  constructor(props: TrackingProviderProps) {
    super(props);
    this.contextData = {
      trackEventUniversalAnalytics: this.trackEventUniversalAnalytics,
      trackEventDataLayerService: this.trackEventDataLayerService,
      trackAddToCartEvent: this.trackAddToCartEvent,
    };
  }

  shouldComponentUpdate(prevProps: TrackingProviderProps) {
    return (
      (this.props.gtmId !== prevProps.gtmId && !this.initialized) ||
      (!this.isDataLayerServiceInitialized &&
        !window.dataLayerService?.initialized &&
        this.isDataLayerServiceEnabled) ||
      this.props.flippyPageNumber !== prevProps.flippyPageNumber
    );
  }

  componentDidUpdate(nextProps: TrackingProviderProps) {
    if (this.props.gtmId && this.props.gtmId !== nextProps.gtmId && !this.initialized) {
      this.injectGtmScriptInHead();
    } else if (
      import.meta.env.VITE_THEME === TENANT.LIDL &&
      !this.isDataLayerServiceInitialized &&
      !window.dataLayerService?.initialized &&
      this.isDataLayerServiceEnabled
    ) {
      this.injectDataLayerServiceScript();
    }
  }

  getScriptWithInjectedGTMID = (): string | undefined => {
    if (this.props.gtmId) {
      return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${this.props.gtmId}');`;
    }
    return undefined;
  };

  injectDataLayerServiceScript = () => {
    if (!this.isDataLayerServiceEnabled) return;

    const { VITE_DATA_LAYER_SERVICE_BASE_URL } = import.meta.env;

    fetch(VITE_DATA_LAYER_SERVICE_BASE_URL + DATA_LAYER_SERVICE_MANIFEST)
      .then(async (data) => {
        if (!data.ok) {
          throw new Error('Failed to load DataLayerService manifest');
        }
        const result: {
          'resources.html'?: {
            file?: string;
          };
        } = await data.json();

        const filename = result?.['resources.html']?.file;
        if (!filename) {
          throw new Error('Could not find a file in DataLayerService manifest.');
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = VITE_DATA_LAYER_SERVICE_BASE_URL + filename;
        document.body.appendChild(script);
        setTimeout(this.trackStoredEventsUniversalAnalytics, 3000);
      })
      .catch((error) => {
        console.error(error);
        this.isDataLayerServiceEnabled = false;
      });
    this.isDataLayerServiceInitialized = true;
  };

  injectGtmScriptInHead = async () => {
    const gtmScript = this.getScriptWithInjectedGTMID();
    if (!gtmScript) {
      return;
    }
    const script = document.createElement('script');

    if (this.props.cookieConsentGtmAttributes) {
      const attributes = this.props.cookieConsentGtmAttributes.split(' ');

      attributes.forEach((attribute) => {
        const [name, value] = attribute.split('=');
        script.setAttribute(name, value.replace(/['"]+/g, ''));
      });
    }

    const scriptText = document.createTextNode(gtmScript);
    script.appendChild(scriptText);
    document.head.appendChild(script);
    this.initialized = true;
    this.trackStoredEventsUniversalAnalytics();
  };

  trackStoredEventsUniversalAnalytics = (): void => {
    const eventQueueLength = this.eventQueueUniversalAnalytics.length;
    if (eventQueueLength > 0) {
      for (let index = 0; index < eventQueueLength; index++) {
        this.trackEventUniversalAnalytics(this.eventQueueUniversalAnalytics[index]);
      }
      this.eventQueueUniversalAnalytics = [];
    }
  };

  trackStoredEventsDataLayerService = (): void => {
    const eventQueueLength = this.eventQueueDataLayerService.length;
    if (eventQueueLength > 0) {
      for (let index = 0; index < eventQueueLength; index++) {
        const { eventCollections, eventInfo, content, products, click, page } =
          this.eventQueueDataLayerService[index];
        this.trackEventDataLayerService(
          eventCollections,
          content,
          eventInfo,
          products,
          click,
          page,
        );
      }
      this.eventQueueDataLayerService = [];
    }
  };

  trackEventUniversalAnalytics = (trackingData: ITRACKING) => {
    // track custom events if GTM is loaded by us or by datalayerService
    if ((this.props.gtmId && this.initialized) || window.dataLayer) {
      const _data = {
        event: 'ga_event',
        eventCategory: trackingData.eventCategory ?? 'flyer_webview',
        eventAction: trackingData.eventAction ?? '',
        eventLabel: trackingData.eventLabel ?? '',
      };
      if (this.props.debugTracking) {
        console.log('trackEventUniversalAnalytics', _data);
      }
      window.dataLayer?.push(_data);
    } else {
      this.eventQueueUniversalAnalytics.push(trackingData);
    }
  };

  trackEventDataLayerService = (
    eventCollections: Collections,
    content?: ContentCollection,
    eventInfo?: EventInfoCollection,
    products?: AProductData[],
    click?: ClickCollection,
    page?: PageCollection,
  ) => {
    if (!this.isDataLayerServiceEnabled) return;

    const { uuid, id, pages, flippyPageNumber, isWideScreen } = this.props;
    const eventAction = eventCollections.eventInfo?.eventAction;
    const newContent: ContentCollection = { ...content };
    const allProducts: ATrackingProduct[] = [...(products || [])];

    // only contains product data
    const isBrowseEvent =
      eventAction === ScenarioName.Flyer_View && eventInfo?.eventInteractionType === 'browse';
    const isProductClickEvent =
      eventAction === ScenarioName.Flyer_Click && eventInfo?.eventInteractionType === 'open';

    const currentVisible = getVisiblePagesAmount(flippyPageNumber, pages.length, isWideScreen);
    const visiblePages = pages.slice(flippyPageNumber - 1, flippyPageNumber - 1 + currentVisible);

    const animatedLinks = visiblePages
      .flatMap((p) => p.links || [])
      .filter((link) => isAnimatedLinkType(link.displayType));

    if (animatedLinks.length > 0) {
      const uniqueAnimationTypes = [...new Set(animatedLinks.map((link) => link.displayType))];
      newContent.contentComponentSource = uniqueAnimationTypes.join(',');
    }

    if (isProductClickEvent && allProducts.length === 1) {
      const clickedProduct = allProducts[0];
      const correspondingAnimatedLink = animatedLinks.find(
        (link) =>
          link.details?.destination?.displayType === 'product' &&
          link.details?.destination?.details?.productId === clickedProduct.productId,
      );

      if (correspondingAnimatedLink) {
        allProducts[0].isAnimated = correspondingAnimatedLink.displayType;
      }
    } else if (isBrowseEvent) {
      const animatedProducts = animatedLinks.reduce<ATrackingProduct[]>((acc, link) => {
        const dest = link.details?.destination;
        if (dest?.displayType === 'product' && dest.details) {
          acc.push({
            ...dest.details,
            isAnimated: link.displayType,
          });
        }
        return acc;
      }, []);

      if (animatedProducts.length > 0) {
        allProducts.push(...animatedProducts);
      }
    }

    const dataLayerServiceEvent: DataLayerServiceEvent = {
      scenarioName: eventCollections.eventInfo?.eventAction as ScenarioName,
      collections: {
        ...eventCollections,
        application: {
          productTeam: 'FLYER',
        },
        eventInfo: {
          ...eventCollections.eventInfo,
          ...eventInfo,
        },
        content: {
          ...eventCollections.content,
          ...newContent,
        },
        ...(allProducts.length > 0 && {
          products: {
            list: this.mapAProductDataToListProduct(allProducts, id, uuid),
          },
        }),
        ...(click && { click }),
        page: {
          ...page,
          pageTitle: uuid,
        },
      },
    };

    if (this.props.debugTracking) {
      console.log('trackEventDataLayerService', dataLayerServiceEvent);
      //set cookie for DataLayerService verbose-mode in mindshift-context
      //cookieName: "dataLayerService" cookieValue: "true"
    }

    window.dataLayerService = window.dataLayerService || [];
    window.dataLayerService.push(dataLayerServiceEvent);
  };

  mapAProductDataToListProduct = (
    products: ATrackingProduct[],
    leafletId: string,
    uuid?: string,
  ): ListProduct[] =>
    products.map((product) => {
      const listProduct: ListProduct = {
        id: product.productId,
        brand: product.brand,
        categoryPrimary: product.categoryPrimary,
        categoryPrimaryPath: product.categoryPrimaryPath,
        currency: product.currencyText,
        name: product.title,
        price: parseFloat(product.price),
        listId: leafletId,
        listName: uuid,
        wonCategoryPrimary: product.wonCategoryPrimary ?? '',
        wonCategoryPrimaryPath: product.wonCategoryPrimaryPath,
      };

      if (product.isAnimated) {
        listProduct.isAnimated = product.isAnimated;
      }

      return listProduct;
    });

  trackAddToCartEvent = (productVariant: Product | ProductVariant) => {
    const productId = productVariant.productId.toString();
    const erpNumber = productVariant.erpNumber.toString();
    const variantId = erpNumber !== 'default' ? erpNumber : productId;

    const _data = {
      event: 'EECaddToCart',
      ecommerce: {
        currencyCode: productVariant.currencySymbol,
        add: {
          products: [
            {
              name: productVariant.title, //Product name
              id: productId, //Product ID
              price: productVariant.price, //Product price
              brand: productVariant.brandName, //Product brand
              category: '', //Product category
              variant: variantId, //variant, eg. 'red'
              quantity: 1, //quantity
              dimension1: '{}', //custom dimension
            },
          ],
        },
      },
    };

    if (this.props.debugTracking) {
      console.log('trackAddToCartEvent', _data);
    }

    window.dataLayer?.push(_data);
  };

  render() {
    if (import.meta.env.VITE_THEME === TENANT.KAUFLAND) {
      return this.props.children;
    }

    return (
      <TrackingContext.Provider value={this.contextData}>
        {this.props.children}
      </TrackingContext.Provider>
    );
  }
}
const mapStateToProps = (state: AppState) => ({
  gtmId: state.appData.gtmId,
  uuid: state.routing.params.uuid,
  debugTracking: state.settings.debugTracking,
  cookieConsentGtmAttributes: state.appData.cookieConsent?.gtmAttributes,
  id: state.appData.id,
  pages: state.appData.pages,
  isWideScreen: state.browser.isWideScreen,
  flippyPageNumber: state.routing.params.page ? parseInt(state.routing.params.page, 10) : 1,
});
export default connect(mapStateToProps, {})(TrackingProvider);

export function withTracking<OriginalProps extends object>(
  UnwrappedComponent: ComponentType<OriginalProps & TrackingContextProps>,
) {
  type NewProps = Omit<OriginalProps, keyof TrackingContextProps> & OwnProps;
  return class WithTracking extends Component<NewProps, {}> {
    static WrappedComponent = UnwrappedComponent;

    render() {
      const Comp = UnwrappedComponent as any;

      return (
        <TrackingContext.Consumer>
          {({ trackEventUniversalAnalytics, trackEventDataLayerService }) => (
            <Comp
              {...this.props}
              trackEventUniversalAnalytics={trackEventUniversalAnalytics}
              trackEventDataLayerService={trackEventDataLayerService}
            />
          )}
        </TrackingContext.Consumer>
      );
    }
  };
}

export function sendWebVitalsToAnalytics({ id, name, value }: Metric) {
  if (typeof window.ga === 'function') {
    ga('send', 'event', {
      eventCategory: 'Web Vitals',
      eventAction: name,
      eventValue: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
      eventLabel: id, // id unique to current page load
      nonInteraction: true, // avoids affecting bounce rate
    });
  }
}
